import { tsMs } from './tts-debug';
import { TtsDebugOverlay } from './tts-debug-overlay';

// A short near-silent 16-bit PCM WAV for the keep-warm loop. Samples are ±1
// LSB (~-90 dBFS, inaudible) but non-zero so the iOS DAC stays powered rather
// than sleeping on digital silence.
function buildNearSilentWav(seconds = 2, sampleRate = 8000): Blob {
  const numSamples = Math.floor(seconds * sampleRate);
  const dataSize = numSamples * 2;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);
  const writeStr = (off: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i));
  };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  writeStr(36, 'data');
  view.setUint32(40, dataSize, true);
  for (let i = 0; i < numSamples; i++) {
    view.setInt16(44 + i * 2, i % 2 === 0 ? 1 : -1, true);
  }
  return new Blob([buffer], { type: 'audio/wav' });
}

/**
 * Level of the keep-warm noise, in dBFS.
 *
 * This is not a cosmetic knob. iOS's own audio-hardware power-down is held
 * off by *any* active render callback regardless of level, which is why
 * -80 dBFS was enough to stop the clipping on the built-in speaker and with
 * the screen locked. A Bluetooth sink is different: it gates on the *decoded
 * level* of what arrives, and the threshold is set by the accessory
 * manufacturer, not by iOS. Below that threshold the earbuds power their amp
 * down during the gaps between utterances and eat the first ~0.3-0.5s of the
 * next one while it wakes (MIC-652).
 *
 * Worse, A2DP to modern earbuds is AAC, a perceptual codec that discards
 * content beneath the absolute threshold of hearing — so a -80/-90 dBFS
 * keep-alive very likely decodes to exact zeros at the sink and does nothing
 * at all. Every keep-alive tool known to work on Bluetooth hardware sits
 * between -60 and -26 dBFS, and the one that documents its reasoning picks
 * audible-band frequencies specifically so codecs do not strip it.
 *
 * -50 dBFS is the starting point, band-limited so it costs as little
 * perceived loudness as possible per dB. Thresholds vary per accessory, so
 * titrate: append ?kwdb=-45 to the URL, or call
 * window.__ttsSetKeepWarmDb(-45) from the console, and step up until the
 * clipping stops.
 */
const KEEP_WARM_DBFS = -50;

/** Above this the hiss stops being background and starts being noticeable. */
const KEEP_WARM_DBFS_CEILING = -25;
const KEEP_WARM_DBFS_FLOOR = -90;

/** Low-passed, because low frequencies survive AAC intact and read as far
 *  quieter than broadband noise at the same dBFS. */
const KEEP_WARM_LOWPASS_HZ = 800;

function dbfsToAmplitude(db: number): number {
  return Math.pow(10, db / 20);
}

const KEEP_WARM_STORAGE_KEY = 'ttsKeepWarmDb';

function clampDb(db: number): number {
  return Math.min(KEEP_WARM_DBFS_CEILING, Math.max(KEEP_WARM_DBFS_FLOOR, db));
}

function readStoredDb(): number | null {
  try {
    const raw = localStorage.getItem(KEEP_WARM_STORAGE_KEY);
    if (raw !== null && raw.trim() !== '' && Number.isFinite(Number(raw))) {
      return Number(raw);
    }
  } catch {
    /* private mode / no storage */
  }
  return null;
}

function storeDb(db: number): void {
  try {
    localStorage.setItem(KEEP_WARM_STORAGE_KEY, String(db));
  } catch {
    /* private mode / no storage — level just won't persist */
  }
}

/**
 * Requested level: `?kwdb=` wins and is remembered, else the remembered value,
 * else the default. Clamped.
 *
 * Persisted deliberately. Titrating this needs a phone and a pair of earbuds,
 * and on iOS there is no console without a cabled Mac and Web Inspector — so
 * the URL param is the only practical channel. But the guest join flow
 * navigates, which would drop a query param before read-along ever starts.
 * Remembering it means one visit to `?kwdb=-40` sets the level for the whole
 * session.
 */
function requestedKeepWarmDb(): number {
  try {
    const raw = new URLSearchParams(location.search).get('kwdb');
    if (raw !== null && raw.trim() !== '' && Number.isFinite(Number(raw))) {
      const db = clampDb(Number(raw));
      storeDb(db);
      return db;
    }
  } catch {
    /* no location (tests) */
  }
  const stored = readStoredDb();
  return stored === null ? KEEP_WARM_DBFS : clampDb(stored);
}

/**
 * Narrow shape of the Web Audio Session API (Safari 17+, iOS 17+). Not in
 * lib.dom yet, so it is declared here rather than pulled in as a global.
 */
type AudioSessionType =
  | 'auto'
  | 'playback'
  | 'transient'
  | 'transient-solo'
  | 'ambient'
  | 'play-and-record';

interface AudioSessionLike {
  type: AudioSessionType;
}

function audioSession(): AudioSessionLike | null {
  const nav = navigator as Navigator & { audioSession?: AudioSessionLike };
  return nav.audioSession ?? null;
}

/**
 * Keep-warm uses two complementary mechanisms because they hold different
 * layers of the iOS audio stack awake:
 *  - AudioContext drives the low-level real-time audio unit, keeping the
 *    DAC hot so TTS playback doesn't lose its first ~0.5s to cold-start
 *    warm-up. iOS suspends it on lock (fine — the clip only matters while
 *    the screen is on; we resume it via `resume()` on visibilitychange).
 *  - A looping near-silent <audio> element is background-eligible media on
 *    iOS, keeping the page resident (and audio alive) through screen lock
 *    and silent gaps between utterances.
 *  - navigator.audioSession.type = 'playback' declares to WebKit that this
 *    page produces sustained output, rather than leaving it on 'auto' where
 *    the session category is inferred and the session is suspended when the
 *    page is backgrounded.
 *
 * The third one exists because the note above — that the clip only matters
 * while the screen is on — turned out to be wrong. Guests listen with the
 * phone locked and earbuds in, which is when the AudioContext is suspended
 * and so the DAC-warm mechanism is not running: every utterance then pays
 * cold-start and loses its first word. Bluetooth makes it worse and
 * consistent, because A2DP route activation costs far more than the
 * built-in speaker (MIC-652).
 */
export class TtsKeepWarm {
  private ctx: AudioContext | null = null;
  private source: AudioBufferSourceNode | null = null;
  private el: HTMLAudioElement | null = null;
  private url: string | null = null;
  /** What the page's audio session was before we claimed it, so stop() can
   *  hand it back rather than leaving every page in playback mode. */
  private previousSessionType: AudioSessionType | null = null;
  /** Held so the level can be titrated live, without a rebuild. */
  private gain: GainNode | null = null;
  /** On-screen diagnostics, only when ?ttsdiag=1 has been asked for. */
  private overlay: TtsDebugOverlay | null = null;

  constructor(private readonly document: Document) {}

  start(): void {
    // Claimed before the two audio sources start, so they are created under
    // the playback category rather than being re-categorised afterwards.
    this.claimPlaybackSession();
    this.startCtx();
    this.startEl();
    this.startOverlay();
    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = 'playing';
    }
  }

  /**
   * Session mechanism: tells WebKit this page produces sustained audio.
   *
   * Under 'auto' WebKit infers the category from what is playing, and
   * AudioContext::shouldOverrideBackgroundPlaybackRestriction only waives the
   * EnteringBackground interruption when the type is 'playback' or
   * 'play-and-record'. So on 'auto' the keep-warm AudioContext is suspended
   * on lock — which is exactly when read-along is being listened to. This
   * does not replace that AudioContext; it is what stops iOS suspending it.
   *
   * Requires iOS >= 17.5: WebKit bug 261554 was 'playback' being ignored for
   * exactly this, fixed in 275558@main and shipped in 17.5. Expect no change
   * on 16.4-17.4, where the API exists but does not waive the interruption.
   * No-op where the API is absent (Android, desktop, iOS < 16.4).
   */
  private claimPlaybackSession(): void {
    const session = audioSession();
    if (!session) {
      console.log(`[TTS ${tsMs()}] keepWarm: no audioSession API, skipping`);
      return;
    }
    try {
      if (this.previousSessionType === null) {
        this.previousSessionType = session.type;
      }
      if (session.type !== 'playback') {
        session.type = 'playback';
      }
      // WebKit makes setType a SILENT no-op when the document lacks the
      // microphone permissions-policy feature — the setter does not throw and
      // the getter pins to 'auto'. A cross-origin iframe needs
      // allow="microphone". Read back rather than assume, because otherwise a
      // page where this never applied is indistinguishable from one where it
      // applied and did not help.
      if (session.type !== 'playback') {
        console.warn(
          `[TTS ${tsMs()}] keepWarm: audioSession claim did NOT take ` +
            `(still '${session.type}') — missing microphone permissions-policy?`,
        );
        return;
      }
      console.log(
        `[TTS ${tsMs()}] keepWarm: audioSession ${this.previousSessionType} -> ` +
          `${session.type} (ua=${navigator.userAgent})`,
      );
    } catch (err) {
      console.warn(`[TTS ${tsMs()}] keepWarm: audioSession claim failed`, err);
    }
  }

  /** Hands the session back, so a page that is not reading along does not
   *  keep interrupting whatever else the phone is playing. */
  private releasePlaybackSession(): void {
    const session = audioSession();
    const previous = this.previousSessionType;
    this.previousSessionType = null;
    if (!session || previous === null) return;
    try {
      session.type = previous;
      console.log(`[TTS ${tsMs()}] keepWarm: audioSession restored to ${previous}`);
    } catch (err) {
      console.warn(`[TTS ${tsMs()}] keepWarm: audioSession restore failed`, err);
    }
  }

  /** DAC-warm mechanism: keeps the head of TTS utterances from being clipped. */
  private startCtx(): void {
    if (this.ctx) return;
    const Ctor =
      (globalThis as Record<string, unknown>)['AudioContext'] ||
      (globalThis as Record<string, unknown>)['webkitAudioContext'];
    if (typeof Ctor !== 'function') {
      console.log(`[TTS ${tsMs()}] keepWarm: no AudioContext available`);
      return;
    }
    try {
      const ctx = new (Ctor as { new (): AudioContext })();
      // 1s of full-scale white noise, attenuated by the gain node below. Kept
      // loud in the buffer so the level is a single runtime-adjustable number
      // rather than something baked into the samples.
      const buffer = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.loop = true;

      const lowpass = ctx.createBiquadFilter();
      lowpass.type = 'lowpass';
      lowpass.frequency.value = KEEP_WARM_LOWPASS_HZ;

      const db = requestedKeepWarmDb();
      const gain = ctx.createGain();
      gain.gain.value = dbfsToAmplitude(db);

      source.connect(lowpass);
      lowpass.connect(gain);
      gain.connect(ctx.destination);
      source.start();
      void ctx.resume();
      this.ctx = ctx;
      this.source = source;
      this.gain = gain;
      this.exposeLevelControl();
      console.log(
        `[TTS ${tsMs()}] keepWarm: AudioContext started (state=${ctx.state}) ` +
          `level=${db}dBFS lowpass=${KEEP_WARM_LOWPASS_HZ}Hz`,
      );
    } catch (err) {
      console.warn(`[TTS ${tsMs()}] keepWarm: AudioContext failed`, err);
    }
  }

  /** Background-survival mechanism: keeps the page resident through lock. */
  private startEl(): void {
    if (this.el) return;
    try {
      const audio = this.document.createElement('audio');
      audio.loop = true;
      audio.hidden = true;
      audio.setAttribute('playsinline', '');
      // Full volume on a ±1-LSB track lands ~-90 dBFS: inaudible, but loud
      // enough that iOS treats it as real playback rather than throttling it
      // as a silent track.
      audio.volume = 1;
      if ('disableRemotePlayback' in audio) {
        (audio as HTMLAudioElement & { disableRemotePlayback: boolean }).disableRemotePlayback = true;
      }
      this.url = URL.createObjectURL(buildNearSilentWav());
      audio.src = this.url;
      this.document.body.appendChild(audio);
      this.el = audio;
      audio.play().then(
        () => console.log(`[TTS ${tsMs()}] keepWarm: <audio> loop playing`),
        (err) => console.warn(`[TTS ${tsMs()}] keepWarm: <audio> play() rejected ${err?.name}: ${err?.message}`),
      );
    } catch (err) {
      console.warn(`[TTS ${tsMs()}] keepWarm: <audio> failed`, err);
    }
  }

  /**
   * Sets the keep-warm level live. Gate thresholds are accessory-specific and
   * undocumented, so finding the right level is empirical — and each attempt
   * otherwise costs a deploy plus a phone plus a pair of earbuds.
   */
  setLevelDb(db: number): number {
    const clamped = clampDb(db);
    storeDb(clamped);
    if (this.gain) {
      this.gain.gain.value = dbfsToAmplitude(clamped);
      console.log(`[TTS ${tsMs()}] keepWarm: level -> ${clamped}dBFS`);
    }
    return clamped;
  }

  private exposeLevelControl(): void {
    try {
      (globalThis as Record<string, unknown>)['__ttsSetKeepWarmDb'] = (db: number) =>
        this.setLevelDb(db);
    } catch {
      /* non-writable global (tests) */
    }
  }

  /** The phone that reproduces MIC-652 has no usable console, so the
   *  diagnostics have to render on the device itself. */
  private startOverlay(): void {
    try {
      if (!TtsDebugOverlay.enabled() || this.overlay) {
        return;
      }
      this.overlay = new TtsDebugOverlay(this.document);
      this.overlay.start(requestedKeepWarmDb());
      console.log(`[TTS ${tsMs()}] keepWarm: diag overlay on`);
    } catch (err) {
      console.warn(`[TTS ${tsMs()}] keepWarm: diag overlay failed`, err);
    }
  }

  stop(): void {
    try {
      this.source?.stop();
    } catch {
      /* already stopped */
    }
    this.source = null;
    if (this.ctx) {
      void this.ctx.close();
      this.ctx = null;
    }
    const audio = this.el;
    if (audio) {
      try {
        audio.pause();
        audio.removeAttribute('src');
        audio.load();
        audio.remove();
      } catch {
        /* ignore */
      }
      this.el = null;
    }
    if (this.url) {
      URL.revokeObjectURL(this.url);
      this.url = null;
    }
    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = 'none';
    }
    this.releasePlaybackSession();
    this.overlay?.stop();
    this.overlay = null;
    console.log(`[TTS ${tsMs()}] keepWarm: stopped`);
  }

  /**
   * iOS suspends the AudioContext on lock; resume it on return so the DAC
   * is hot again before the user resumes the conversation (else the first
   * utterance after unlock loses its head to cold-start warm-up).
   */
  resume(): void {
    // Re-asserted because we do not know that the type survives a session
    // suspend/restore intact, and getting it wrong costs the first word of
    // every utterance. Idempotent when it did survive.
    this.claimPlaybackSession();
    if (this.ctx && this.ctx.state === 'suspended') {
      void this.ctx.resume();
    }
    if (this.el?.paused) {
      this.el.play().catch(() => {});
    }
  }
}
